// Verbindung zum Weltserver. Kümmert sich um vier Dinge:
//   1. Uhrensynchronisation (Konzept §5: eine Weltzeit für alle)
//   2. Snapshots (Zug, andere Spieler)
//   3. Graffiti-Abos und -Übertragung
//   4. Funk
//
// Wichtig: Der Client rechnet Zug und Zeit *selbst* aus dem Fahrplan. Der
// Snapshot ist nur Korrektur. Damit ruckelt nichts bei 20 Hz und das Spiel
// bleibt spielbar, wenn die Verbindung kurz weg ist.

import { MSG, decode, encode, ERR } from '../../shared/protocol.js';
import * as P from '../../shared/protocol.js';
import { PROTOCOL_VERSION, CLIENT_INPUT_HZ } from '../../shared/constants.js';
import { nowWorldSeconds } from '../../shared/worldtime.js';

const RECONNECT_DELAYS = [500, 1000, 2000, 4000, 8000, 15000];

export class Connection {
  constructor(url = null) {
    this.url = url ?? defaultUrl();
    this.ws = null;
    this.sessionId = null;
    this.seasonId = null;
    this.connected = false;
    this.everConnected = false;
    this.attempt = 0;

    // Schreibrecht (§13). Default true, bis Welcome etwas anderes sagt —
    // entspricht dem Server-Default `open`, damit Offline/vor-Welcome nichts
    // kaputtgeht. Ohne Recht: malen wird abgewiesen, lesen bleibt.
    this.canWrite = true;
    /** Einmalig true, wenn der Server Schreiben verweigert hat. HUD erklärt. */
    this.writeDeniedOnce = false;
    this._writeToken = readWriteToken();

    // Uhrensynchronisation: Offset zwischen lokaler und Serverzeit.
    this.clockOffsetMs = 0;
    this.rtt = 0;
    this._samples = [];
    this._pingTimer = null;

    // Zustand vom Server.
    this.train = null;
    this.remotePlayers = new Map(); // id -> { pos, rot, anim, carId, interp }

    // Callbacks, die Systeme registrieren.
    this.handlers = {
      welcome: [], paintData: [], paintAdd: [], radioRx: [],
      peerLeft: [], status: [], rtcSignal: [], access: [], error: [],
    };

    this._sendQueue = [];
    this._lastInputSent = 0;
    this._subscribed = new Set();
    this._pendingSubs = new Set();
    this.bytesIn = 0;
    this.bytesOut = 0;
    this.msgsIn = 0;
  }

  on(event, fn) {
    this.handlers[event]?.push(fn);
    return this;
  }

  #emit(event, ...args) {
    for (const fn of this.handlers[event] ?? []) {
      try { fn(...args); } catch (err) { console.error(`[net] Handler ${event}:`, err); }
    }
  }

  connect() {
    this.#emit('status', this.everConnected ? 'verbinde neu …' : 'verbinde …');
    try {
      this.ws = new WebSocket(this.url);
    } catch (err) {
      this.#scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.connected = true;
      this.everConnected = true;
      this.attempt = 0;
      this.#send(P.hello(Date.now(), null, this._writeToken));
      this.#startPing();
      // Warteschlange leeren.
      for (const msg of this._sendQueue) this.#send(msg);
      this._sendQueue.length = 0;
      // Abos nach einem Reconnect erneuern.
      if (this._subscribed.size > 0) {
        const ids = [...this._subscribed];
        this._subscribed.clear();
        this.subscribe(ids);
      }
    };

    this.ws.onmessage = (ev) => {
      this.msgsIn++;
      this.bytesIn += typeof ev.data === 'string' ? ev.data.length : 0;
      const msg = decode(ev.data);
      if (msg) this.#handle(msg);
    };

    this.ws.onclose = () => {
      this.connected = false;
      this.#stopPing();
      this.remotePlayers.clear();
      this.#emit('status', 'Verbindung verloren');
      this.#scheduleReconnect();
    };

    this.ws.onerror = () => { /* onclose folgt und macht die Arbeit */ };
  }

  #scheduleReconnect() {
    const delay = RECONNECT_DELAYS[Math.min(this.attempt, RECONNECT_DELAYS.length - 1)];
    this.attempt++;
    setTimeout(() => this.connect(), delay);
  }

  #handle(msg) {
    switch (msg.t) {
      case MSG.WELCOME: {
        if (msg.v !== PROTOCOL_VERSION) {
          console.warn(`[net] Protokoll-Unterschied: Server v${msg.v}, Client v${PROTOCOL_VERSION}`);
        }
        this.sessionId = msg.id;
        this.seasonId = msg.season ?? null;
        // w fehlt bei alten Servern → schreiben erlaubt (Abwärtskompatibilität).
        this.canWrite = msg.w == null ? true : !!msg.w;
        this.#applyClock(msg.st, msg.ws, 0);
        for (const p of msg.players ?? []) this.#applyRemote(p);
        this.#emit('welcome', msg);
        this.#emit('status', 'verbunden');
        this.#emit('access', this.canWrite);
        break;
      }

      case MSG.ACCESS: {
        this.canWrite = !!msg.w;
        this.#emit('access', this.canWrite);
        break;
      }

      case MSG.ERROR: {
        if (msg.code === ERR.NO_WRITE || msg.code === ERR.BANNED) {
          this.canWrite = false;
          this.writeDeniedOnce = true;
          this.#emit('access', false);
        }
        this.#emit('error', msg.code, msg.detail);
        break;
      }

      case MSG.PONG: {
        const now = Date.now();
        const rtt = now - msg.ct;
        // Serverzeit zum Empfangszeitpunkt = st + halbe Laufzeit.
        this.#applyClock(msg.st, msg.ws, rtt);
        this.rtt = rtt;
        break;
      }

      case MSG.SNAPSHOT: {
        this.train = msg.tr;
        const seen = new Set();
        for (const p of msg.pl ?? []) {
          this.#applyRemote(p);
          seen.add(p.id);
        }
        // Wer nicht mehr im Snapshot ist, ist außer Reichweite (oder weg).
        for (const id of this.remotePlayers.keys()) {
          if (!seen.has(id)) {
            const rp = this.remotePlayers.get(id);
            rp.missing = (rp.missing ?? 0) + 1;
            if (rp.missing > 40) this.remotePlayers.delete(id);
          }
        }
        break;
      }

      case MSG.PAINT_DATA:
        this.#emit('paintData', msg.s, msg.strokes ?? []);
        break;

      case MSG.PAINT_ADD:
        this.#emit('paintAdd', msg.st);
        break;

      case MSG.RADIO_RX:
        this.#emit('radioRx', msg);
        break;

      case MSG.PEER_LEFT:
        this.remotePlayers.delete(msg.id);
        this.#emit('peerLeft', msg.id);
        break;

      case MSG.RTC_SIGNAL:
        this.#emit('rtcSignal', msg.from, msg.d);
        break;

      default:
        break;
    }
  }

  #applyRemote(p) {
    let rp = this.remotePlayers.get(p.id);
    if (!rp) {
      rp = {
        id: p.id,
        // Zwei Zustände für Interpolation: wo er war, wo er hin soll.
        from: { x: p.p[0], y: p.p[1], z: p.p[2] },
        to: { x: p.p[0], y: p.p[1], z: p.p[2] },
        pos: { x: p.p[0], y: p.p[1], z: p.p[2] },
        yaw: p.r?.[0] ?? 0,
        targetYaw: p.r?.[0] ?? 0,
        pitch: p.r?.[1] ?? 0,
        anim: p.a ?? 0,
        carId: p.c ?? null,
        t: 0,
        isNew: true,
      };
      this.remotePlayers.set(p.id, rp);
    } else {
      rp.from.x = rp.pos.x; rp.from.y = rp.pos.y; rp.from.z = rp.pos.z;
      rp.to.x = p.p[0]; rp.to.y = p.p[1]; rp.to.z = p.p[2];
      rp.targetYaw = p.r?.[0] ?? rp.targetYaw;
      rp.pitch = p.r?.[1] ?? rp.pitch;
      rp.anim = p.a ?? 0;
      rp.carId = p.c ?? null;
      rp.t = 0;
      rp.missing = 0;
    }
  }

  /** Interpolation der Remote-Spieler. Jeden Frame aufrufen. */
  interpolate(dt) {
    const step = 1000 / 20; // Snapshotrate
    for (const rp of this.remotePlayers.values()) {
      rp.t = Math.min(1, rp.t + (dt * 1000) / step);
      const e = rp.t * rp.t * (3 - 2 * rp.t);
      rp.pos.x = rp.from.x + (rp.to.x - rp.from.x) * e;
      rp.pos.y = rp.from.y + (rp.to.y - rp.from.y) * e;
      rp.pos.z = rp.from.z + (rp.to.z - rp.from.z) * e;
      // Kürzester Winkelweg, damit sich niemand einmal ganz herumdreht.
      let d = rp.targetYaw - rp.yaw;
      while (d > Math.PI) d -= Math.PI * 2;
      while (d < -Math.PI) d += Math.PI * 2;
      rp.yaw += d * Math.min(1, dt * 12);
    }
  }

  #applyClock(serverTime, serverWorldSeconds, rtt) {
    // Offset so, dass Date.now() + offset ≈ Serverzeit.
    const offset = serverTime + rtt / 2 - Date.now();
    this._samples.push(offset);
    if (this._samples.length > 9) this._samples.shift();
    // Median statt Mittelwert: ein einzelner Ausreißer soll die Uhr nicht
    // verschieben.
    const sorted = [...this._samples].sort((a, b) => a - b);
    this.clockOffsetMs = sorted[Math.floor(sorted.length / 2)];
  }

  #startPing() {
    this.#stopPing();
    // Anfangs schnell messen, damit die Uhr sofort steht, dann selten.
    let count = 0;
    const doPing = () => {
      if (!this.connected) return;
      this.#send(P.ping(Date.now()));
      count++;
      this._pingTimer = setTimeout(doPing, count < 5 ? 250 : 8000);
    };
    doPing();
  }

  #stopPing() {
    if (this._pingTimer) clearTimeout(this._pingTimer);
    this._pingTimer = null;
  }

  /** Weltzeit, serverkorrigiert. Der einzige Zeitgeber des Spiels. */
  worldSeconds() {
    return nowWorldSeconds(this.clockOffsetMs);
  }

  #send(msg) {
    if (!this.connected || this.ws?.readyState !== 1) return false;
    const raw = encode(msg);
    this.bytesOut += raw.length;
    try {
      this.ws.send(raw);
      return true;
    } catch {
      return false;
    }
  }

  /** Nachricht senden, bei fehlender Verbindung puffern (begrenzt). */
  send(msg) {
    if (this.#send(msg)) return;
    // Striche und Funk sind wertvoll: puffern. Positionen nicht.
    if (msg.t === MSG.PAINT_STROKE || msg.t === MSG.RADIO_TX) {
      this._sendQueue.push(msg);
      if (this._sendQueue.length > 200) this._sendQueue.shift();
    }
  }

  /** Position senden — nur mit begrenzter Rate. */
  sendInput(state) {
    const now = performance.now();
    if (now - this._lastInputSent < 1000 / CLIENT_INPUT_HZ) return;
    this._lastInputSent = now;
    this.#send(P.input(state));
  }

  subscribe(surfaceIds) {
    const fresh = surfaceIds.filter((id) => !this._subscribed.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) this._subscribed.add(id);
    // In Häppchen, damit eine Nachricht nicht riesig wird.
    for (let i = 0; i < fresh.length; i += 48) {
      this.send(P.paintSub(fresh.slice(i, i + 48)));
    }
  }

  sendStroke(stroke) {
    // Ohne Schreibrecht gar nicht erst senden — Server würde ERROR zurückgeben.
    // Trotzdem markieren, damit das HUD die einmalige Erklärung zeigen kann.
    if (this.canWrite === false) {
      this.writeDeniedOnce = true;
      this.#emit('error', ERR.NO_WRITE, null);
      return;
    }
    this.send(P.paintStroke(stroke));
  }

  sendReport(surfaceId, reason = null) {
    this.send(P.report(surfaceId, reason));
  }

  tuneRadio(channel) {
    this.send(P.radioTune(channel));
  }

  sendRadio(channel, text) {
    // Funk braucht kein Schreibrecht (flüchtig, kein Gedächtnis — siehe Server).
    this.send(P.radioTx(channel, text));
  }

  stats() {
    return {
      connected: this.connected,
      id: this.sessionId,
      rtt: Math.round(this.rtt),
      offset: Math.round(this.clockOffsetMs),
      peers: this.remotePlayers.size,
      canWrite: this.canWrite,
      kbIn: (this.bytesIn / 1024).toFixed(1),
      kbOut: (this.bytesOut / 1024).toFixed(1),
    };
  }
}

function defaultUrl() {
  const loc = window.location;
  const proto = loc.protocol === 'https:' ? 'wss:' : 'ws:';
  // Im Dev-Modus läuft Vite auf 5173 und der Weltserver auf 8787.
  if (loc.port === '5173' || loc.port === '4173') {
    return `${proto}//${loc.hostname}:8787/ws`;
  }
  return `${proto}//${loc.host}/ws`;
}

/**
 * Schreibschlüssel aus URL oder localStorage — die Client-Seite der
 * austauschbaren Entitlement-Stelle. Kein Kauf-UI, nur der Haken.
 */
function readWriteToken() {
  try {
    const q = new URLSearchParams(window.location.search);
    const fromUrl = q.get('writeToken') || q.get('tok');
    if (fromUrl) {
      try { localStorage.setItem('th_write_token', fromUrl); } catch { /* privat */ }
      return fromUrl;
    }
    return localStorage.getItem('th_write_token');
  } catch {
    return null;
  }
}
